import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { festAPI } from '@/lib/api';

/* Sommerfest-Festkarte — öffentliche Gastseiten, KEIN Login.
 *
 *   /fest           Formular (Haftung + Los + Interesse), mobile first —
 *                   die Leute stehen in der Sonne am Eingang.
 *   /fest/k/:code   Bestätigungsscreen: Losnummer groß, QR für die
 *                   Mitarbeiter-Scans, Challenge-Fortschritt (pollt alle
 *                   20 s und bei Fokus, damit ein frischer Haken sofort
 *                   sichtbar wird).
 *
 * Der karten_code ist die einzige "Authentifizierung" des Gastes — der
 * Server liefert auf diesem Weg bewusst keinen Nachnamen, keine Nummer,
 * keine E-Mail. P1-Look wie /seminar: dunkel, Anton, Rot #f5821f.
 */

const ROT = '#f5821f';
const RETRY_KEY = 'fest_formular';

const INTERESSEN = [
  ['kinder', 'Kindertraining'],
  ['frauen', 'Frauentraining'],
  ['erwachsene', 'Kampfsport / Fitness'],
  ['small_group', 'Small Group – Founding-Rate'],
  ['nur_verlosung', 'Nur Verlosung'],
];

function herkunftAusUrl() {
  const p = new URLSearchParams(window.location.search);
  const raus = {};
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
                   'utm_term', 'fbclid', 'gclid', 'ttclid']) {
    const v = p.get(k);
    if (v) raus[k] = v;
  }
  // Kurzquelle + Station der gedruckten QR-Codes (?q=fest-eingang&st=ring):
  // Whitelist prueft das Backend (herkunft.py), hier nur Form und Laenge.
  for (const k of ['q', 'st']) {
    const v = (p.get(k) || '').toLowerCase();
    if (/^[a-z0-9-]{1,32}$/.test(v)) raus[k] = v;
  }
  return raus;
}

/* ------------------------------------------------------------- Formular */

export function FestFormular() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(() => {
    // Netzfehler-Schutz: gehaltene Formulardaten wiederherstellen
    try {
      const alt = JSON.parse(sessionStorage.getItem(RETRY_KEY) || 'null');
      if (alt) return alt;
    } catch { /* egal */ }
    return {
      vorname: '', nachname: '', telefon: '', email: '',
      interesse: [], kinder: [],
      haftung: false, datenschutz: false, kontakt: false, website: '',
    };
  });
  const [fehler, setFehler] = useState('');
  const [sende, setSende] = useState(false);
  const herkunft = useMemo(herkunftAusUrl, []);

  useEffect(() => {
    festAPI.publicConfig().then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const set = (feld, wert) => setForm((f) => ({ ...f, [feld]: wert }));
  const nurVerlosung = form.interesse.length > 0 &&
    form.interesse.every((i) => i === 'nur_verlosung');

  const toggleInteresse = (id) => {
    setForm((f) => {
      const neu = f.interesse.includes(id)
        ? f.interesse.filter((x) => x !== id)
        : [...f.interesse, id];
      const kinder = neu.includes('kinder') ? f.kinder : [];
      return { ...f, interesse: neu, kinder };
    });
  };

  const setKinderAnzahl = (n) => {
    setForm((f) => ({
      ...f,
      kinder: Array.from({ length: n }, (_, i) => f.kinder[i] ?? { alter: '' }),
    }));
  };

  async function absenden() {
    setFehler('');
    const telefonZiffern = (form.telefon.match(/\d/g) || []).length;
    if (form.vorname.trim().length < 2 || form.nachname.trim().length < 2) {
      setFehler('Bitte Vor- und Nachnamen eintragen.'); return;
    }
    if (telefonZiffern < 8) {
      setFehler('Bitte eine erreichbare Handynummer eintragen.'); return;
    }
    if (form.interesse.length === 0) {
      setFehler('Bitte wähle aus, wofür du dich interessierst — oder "Nur Verlosung".'); return;
    }
    if (form.interesse.includes('kinder') &&
        (form.kinder.length === 0 || form.kinder.some((k) => k.alter === ''))) {
      setFehler('Bitte das Alter jedes Kindes angeben.'); return;
    }
    if (!nurVerlosung && !form.haftung) {
      setFehler('Für Training und Challenges brauchen wir die Haftungserklärung.'); return;
    }
    if (!form.datenschutz) {
      setFehler('Bitte den Datenschutzhinweis bestätigen.'); return;
    }
    setSende(true);
    try { sessionStorage.setItem(RETRY_KEY, JSON.stringify(form)); } catch { /* egal */ }
    try {
      const r = await festAPI.karteAnlegen({
        vorname: form.vorname.trim(), nachname: form.nachname.trim(),
        telefon: form.telefon.trim(), email: form.email.trim(),
        interesse: form.interesse,
        kinder: form.kinder.map((k) => ({ alter: Number(k.alter) })),
        haftung_akzeptiert: form.haftung,
        datenschutz_akzeptiert: form.datenschutz,
        kontakt_erlaubt: form.kontakt,
        website: form.website, herkunft,
      });
      try { sessionStorage.removeItem(RETRY_KEY); } catch { /* egal */ }
      window.location.assign(`/fest/k/${r.data.karten_code}`);
    } catch (err) {
      setSende(false);
      setFehler(err?.response?.data?.detail ||
        'Das hat gerade nicht geklappt — deine Eingaben sind gespeichert, tippe einfach nochmal auf den Knopf.');
    }
  }

  return (
    <div className="fk-root">
      <style>{CSS}</style>
      <header className="fk-kopf">
        <img src="/logo.png" alt="P1 Performance Center" className="fk-logo"
             onError={(e) => { e.target.style.display = 'none'; }} />
        <p className="fk-eyebrow">{config?.titel || 'P1 Sommerfest 2026'}</p>
        <h1>Deine Festkarte<span style={{ color: ROT }}>.</span></h1>
        <p className="fk-lead">
          Einmal ausfüllen — dein Los für die Verlosung um 19:40, deine
          Challenge-Karte und dein Zugang zu den Schnuppertrainings.
        </p>
      </header>

      <main className="fk-formular">
        <div className="fk-grid">
          <label className="fk-feld">
            <span>Vorname *</span>
            <input value={form.vorname} onChange={(e) => set('vorname', e.target.value)}
                   autoComplete="given-name" />
          </label>
          <label className="fk-feld">
            <span>Nachname *</span>
            <input value={form.nachname} onChange={(e) => set('nachname', e.target.value)}
                   autoComplete="family-name" />
          </label>
        </div>
        <label className="fk-feld">
          <span>Handynummer *</span>
          <input type="tel" inputMode="tel" placeholder="z. B. 0176 1234567"
                 value={form.telefon} onChange={(e) => set('telefon', e.target.value)}
                 autoComplete="tel" />
        </label>
        <label className="fk-feld">
          <span>E-Mail (optional — für die Fotos vom Fest)</span>
          <input type="email" inputMode="email" value={form.email}
                 onChange={(e) => set('email', e.target.value)} autoComplete="email" />
        </label>

        <p className="fk-frage">Ich interessiere mich für:</p>
        <div className="fk-chips">
          {INTERESSEN.map(([id, label]) => (
            <button key={id} type="button"
                    className={form.interesse.includes(id) ? 'fk-chip fk-chip-an' : 'fk-chip'}
                    onClick={() => toggleInteresse(id)}>
              {label}
            </button>
          ))}
        </div>

        {form.interesse.includes('kinder') && (
          <div className="fk-kinder">
            <label className="fk-feld">
              <span>Anzahl Kinder</span>
              <select value={form.kinder.length}
                      onChange={(e) => setKinderAnzahl(Number(e.target.value))}>
                <option value={0}>bitte wählen</option>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <div className="fk-grid">
              {form.kinder.map((k, i) => (
                <label className="fk-feld" key={i}>
                  <span>Alter Kind {i + 1}</span>
                  <input type="number" inputMode="numeric" min="0" max="17"
                         value={k.alter}
                         onChange={(e) => setForm((f) => {
                           const kinder = [...f.kinder];
                           kinder[i] = { alter: e.target.value };
                           return { ...f, kinder };
                         })} />
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Honigtopf */}
        <input className="fk-hp" tabIndex={-1} autoComplete="off" aria-hidden="true"
               value={form.website} onChange={(e) => set('website', e.target.value)} />

        {!nurVerlosung && form.interesse.length > 0 && (
          <section className="fk-haftung" data-fehler={!!fehler && !form.haftung}>
            <h2>Haftungserklärung</h2>
            <p className="fk-haftung-text">
              {config?.texte?.haftung ||
                'Ich nehme auf eigene Verantwortung an den Schnuppertrainings und '
                + 'Fitness-Challenges des P1 Sommerfests teil. Mir ist bekannt, dass '
                + 'Kampfsport und körperliches Training mit Verletzungsrisiken '
                + 'verbunden sind. Ich bin gesund und körperlich in der Lage '
                + 'teilzunehmen. Für minderjährige Kinder erkläre ich dies als '
                + 'erziehungsberechtigte Person. Die P1 Performance Center GmbH '
                + 'haftet nur bei Vorsatz und grober Fahrlässigkeit.'}
            </p>
            <label className="fk-haken fk-haken-haftung">
              <input type="checkbox" checked={form.haftung}
                     onChange={(e) => set('haftung', e.target.checked)} />
              <span>Ich habe die Haftungserklärung gelesen und akzeptiere sie.</span>
            </label>
          </section>
        )}
        <label className="fk-haken">
          <input type="checkbox" checked={form.datenschutz}
                 onChange={(e) => set('datenschutz', e.target.checked)} />
          <span>
            {config?.texte?.datenschutz || 'Meine Daten werden zur Durchführung des Sommerfests verarbeitet.'}{' '}
            <a href="/datenschutz" target="_blank" rel="noreferrer">Datenschutzerklärung</a>
          </span>
        </label>
        <label className="fk-haken">
          <input type="checkbox" checked={form.kontakt}
                 onChange={(e) => set('kontakt', e.target.checked)} />
          <span>{config?.texte?.kontakt ||
            'Ja, P1 darf mich per WhatsApp / Telefon zum Probetraining kontaktieren.'}</span>
        </label>

        {fehler && <p className="fk-fehler">{fehler}</p>}

        <button className="fk-cta" onClick={absenden} disabled={sende}>
          {sende ? 'Wird erstellt …' : 'Festkarte erstellen'}
        </button>
        <p className="fk-fuss">
          Kostenlos · dein Los wird bei der Verlosung um 19:40 angesagt
        </p>
      </main>
    </div>
  );
}

/* ------------------------------------------------ Founding Small Group */

/* Zwoelf Plaetze, nur am Fest. Der Zaehler kommt vom Server und schrumpft
   sichtbar — die Karte pollt ohnehin alle 20 Sekunden. Reservierung, kein
   Vertrag: hier wird nichts abgeschlossen und nichts bezahlt. */
function FoundingAbschnitt({ karte, code, nachAktion }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const f = karte.founding;

  // Nach dem Founding-Ende ist der Abschnitt weg. Der Knopf greift dann auch
  // serverseitig nicht mehr — das hier ist nur die Anzeige.
  if (!f || !f.offen) return null;

  const sichern = async () => {
    setBusy(true); setFehler('');
    try {
      await festAPI.foundingSichernGast(code);
      await nachAktion();
    } catch (err) {
      setFehler(err?.response?.data?.detail
        || 'Hat gerade nicht geklappt — bitte am P1-Stand melden.');
    }
    setBusy(false);
  };

  if (f.mein_status) {
    return (
      <section className="fk-founding" data-testid="fk-founding">
        <h2>{f.titel}</h2>
        <p className="fk-founding-ok" data-testid="fk-founding-ok">
          {f.mein_status === 'platz' ? f.bestaetigung : f.bestaetigung_warteliste}
        </p>
      </section>
    );
  }

  const voll = f.warteliste_aktiv;
  return (
    <section className="fk-founding" data-testid="fk-founding">
      <h2>{f.titel}</h2>
      <p className="fk-founding-text">{f.text}</p>
      {!voll && (
        <p className="fk-founding-zaehler" data-testid="fk-founding-zaehler">
          Noch <b>{f.frei}</b> von {f.gesamt} Plätzen
        </p>
      )}
      {voll && (
        <p className="fk-founding-text">Alle {f.gesamt} Plätze sind vergeben.</p>
      )}
      {fehler && <p className="fk-fehler">{fehler}</p>}
      <button className="fk-cta" disabled={busy} onClick={sichern}
              data-testid="fk-founding-knopf">
        {busy ? 'Einen Moment …' : (voll ? 'Warteliste' : 'Platz sichern')}
      </button>
      <p className="fk-founding-fuss">
        Unverbindliche Reservierung — kein Vertrag, keine Zahlung auf dieser Seite.
      </p>
    </section>
  );
}

/* --------------------------------------------------- Bestätigungsscreen */

export function FestKarte() {
  const { code } = useParams();
  const [karte, setKarte] = useState(null);
  const [fehler, setFehler] = useState('');
  const [kopiert, setKopiert] = useState(false);
  const timerRef = useRef(null);

  const laden = useCallback(async () => {
    try {
      const r = await festAPI.karteOeffentlich(code);
      setKarte(r.data);
      setFehler('');
    } catch {
      setFehler('Diese Festkarte wurde nicht gefunden.');
    }
  }, [code]);

  useEffect(() => {
    laden();
    timerRef.current = setInterval(laden, 20000);   // Haken sofort sichtbar
    const beiFokus = () => laden();
    window.addEventListener('focus', beiFokus);
    document.addEventListener('visibilitychange', beiFokus);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener('focus', beiFokus);
      document.removeEventListener('visibilitychange', beiFokus);
    };
  }, [laden]);

  const scanUrl = `${window.location.origin}/fest/scan?c=${code}`;

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2500);
    } catch { /* Clipboard verweigert — egal */ }
  };

  if (fehler) {
    return (
      <div className="fk-root"><style>{CSS}</style>
        <div className="fk-mitte"><p className="fk-fehler">{fehler}</p>
          <a className="fk-cta" href="/fest">Neue Festkarte erstellen</a>
        </div>
      </div>
    );
  }
  if (!karte) {
    return <div className="fk-root"><style>{CSS}</style>
      <div className="fk-mitte"><p className="fk-lade">Festkarte wird geladen …</p></div></div>;
  }

  const gemacht = new Set(karte.challenges.map((c) => c.challenge_id));
  const minimum = karte.config.verlosung_min_challenges;

  return (
    <div className="fk-root">
      <style>{CSS}</style>
      <main className="fk-karte">
        <div className="fk-dankekarte">
          <img src="/logo.png" alt="P1 Performance Center" className="fk-logo fk-logo-danke"
               onError={(e) => { e.target.style.display = 'none'; }} />
          <p className="fk-eyebrow">{karte.config.titel}</p>
          <h1 className="fk-hallo">Danke, {karte.vorname}<span style={{ color: ROT }}>!</span></h1>
          <p className="fk-danketext">
            Deine Festkarte ist da — hier ist alles, was du am Fest brauchst.
          </p>

          <div className="fk-los">
            <span className="fk-los-label">Deine Losnummer</span>
            <span className="fk-los-nr">#{karte.losnummer}</span>
          </div>

          <div className="fk-qr">
            <img src={festAPI.qrUrl(scanUrl)} alt="Dein Festkarten-QR-Code"
                 width={220} height={220} />
          </div>
          {karte.eingecheckt && (
            <div className="fk-eingecheckt" data-testid="fk-eingecheckt">
              ✓ Am Eingang eingecheckt
            </div>
          )}
          <p className="fk-hinweis">
            Zeig diesen Screen am Eingang für dein Band, an der Challenge-Ecke
            und am P1-Stand.
          </p>
        </div>

        <FoundingAbschnitt karte={karte} code={code} nachAktion={laden} />

        <section className="fk-fortschritt">
          <h2>Deine Challenges</h2>
          <p className="fk-zaehler">
            {Math.min(gemacht.size, minimum)} von {minimum} für die Verlosung
            {gemacht.size >= minimum && ' — du bist im Lostopf! 🎉'}
          </p>
          <div className="fk-kacheln">
            {karte.config.challenges.map((c) => (
              <div key={c.id}
                   className={gemacht.has(c.id) ? 'fk-kachel fk-kachel-an' : 'fk-kachel'}>
                <span className="fk-kachel-haken">{gemacht.has(c.id) ? '✓' : ''}</span>
                {c.name}
              </div>
            ))}
          </div>
        </section>

        <FestBuchung karte={karte} code={code} nachBuchung={laden} />

        {karte.gymfights && (
          <p className="fk-pt-ok" data-testid="fk-gymfights">✓ Gymfights-Ticket 26.09. reserviert — am P1-Stand vermerkt</p>
        )}

        <button className="fk-cta fk-cta-line" onClick={kopieren}>
          {kopiert ? 'Link kopiert ✓' : 'Link kopieren (damit du den Screen wiederfindest)'}
        </button>
        <p className="fk-fuss">
          Tipp: Über „Teilen → Zum Home-Bildschirm" bleibt die Karte griffbereit.
        </p>
      </main>
    </div>
  );
}

/* ---------------------------------------------------- Probetraining buchen
   12.09.-Erweiterung: Wer nach dem Schnuppertraining im Ring begeistert ist,
   tippt in DIESEM Moment oder gar nicht — deshalb direkt auf der Karte,
   drei Schritte, grosse Flaechen. Netz im Park kann einbrechen: jeder
   Fehler zeigt eine klare Meldung und einen Nochmal-Knopf. */
function FestBuchung({ karte, code, nachBuchung }) {
  const [schritt, setSchritt] = useState(0);      // 0 zu, 1 Alter, 2 Termin
  const [alter, setAlter] = useState(null);
  const [kurse, setKurse] = useState(null);
  const [kindName, setKindName] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [fertig, setFertig] = useState(null);

  const buchungen = (karte.probetrainings || []).filter((b) => b.status !== 'storniert');
  const nochFrei = buchungen.length < (karte.max_probetrainings || 2);
  const kinderGruppe = alter && alter.startsWith('kind') || alter === 'minis_3_5';

  const kurseLaden = async (gruppe) => {
    setAlter(gruppe); setKurse(null); setFehler('');
    try {
      const r = await festAPI.gastKurse(code, gruppe);
      setKurse(r.data.kurse);
      setSchritt(2);
    } catch {
      setFehler('Keine Verbindung — das Netz im Park schwächelt gerade. Gleich nochmal probieren.');
    }
  };

  const buchen = async (terminId) => {
    if (busy) return;
    setBusy(true); setFehler('');
    try {
      const r = await festAPI.gastBuchen(code, {
        termin_id: terminId,
        kind_vorname: kinderGruppe ? kindName.trim() : '',
      });
      setFertig(r.data.anzeige);
      setSchritt(0); setAlter(null); setKurse(null); setKindName('');
      nachBuchung();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setFehler(typeof detail === 'string' ? detail
        : 'Keine Verbindung — die Buchung ist NICHT gespeichert. Bitte nochmal tippen.');
    } finally {
      setBusy(false);
    }
  };

  const stornieren = async (buchungId) => {
    if (!window.confirm('Diesen Termin wirklich stornieren?')) return;
    try {
      await festAPI.gastStorno(code, buchungId);
      nachBuchung();
    } catch {
      setFehler('Stornieren hat nicht geklappt — bitte nochmal versuchen.');
    }
  };

  return (
    <section className="fk-pt">
      <h2>Dein Probetraining</h2>

      {buchungen.map((b) => (
        <div key={b.buchung_id} className="fk-pt-buchung">
          <p><b>{b.kurs}</b>{b.teilnehmer ? ` für ${b.teilnehmer}` : ''}<br />
            {b.datum} um {b.uhrzeit} Uhr · P1 Performance Center</p>
          <button className="fk-pt-storno" onClick={() => stornieren(b.buchung_id)}>
            Stornieren
          </button>
        </div>
      ))}

      {fertig && (
        <p className="fk-pt-ok">✓ Gebucht: <b>{fertig.kurs}</b> am {fertig.datum} um{' '}
          {fertig.uhrzeit} Uhr. Wir melden uns mit der Bestätigung!</p>
      )}

      {fehler && (
        <div className="fk-pt-fehler">
          <p>{fehler}</p>
          <button className="fk-cta" onClick={() => {
            setFehler('');
            if (schritt === 2 && alter) kurseLaden(alter);
          }}>Nochmal versuchen</button>
        </div>
      )}

      {nochFrei && schritt === 0 && !fehler && (
        <button className="fk-cta fk-cta-gross" onClick={() => setSchritt(1)}>
          {buchungen.length ? 'Noch ein Probetraining buchen'
                            : 'Kostenloses Probetraining buchen'}
        </button>
      )}
      {!nochFrei && (
        <p className="fk-fuss">Zwei Termine sind gebucht — mehr geht über das
          Team am Stand.</p>
      )}

      {schritt === 1 && (
        <div className="fk-pt-schritt">
          <p className="fk-pt-frage">Für wen ist das Training?</p>
          {(karte.altersgruppen || []).map((g) => (
            <button key={g.id} className="fk-pt-option" onClick={() => kurseLaden(g.id)}>
              {g.label}
            </button>
          ))}
        </div>
      )}

      {schritt === 2 && (
        <div className="fk-pt-schritt">
          {kinderGruppe && (
            <input className="fk-pt-input" placeholder="Vorname des Kindes"
                   value={kindName} onChange={(e) => setKindName(e.target.value)} />
          )}
          <p className="fk-pt-frage">Wähle deinen Termin:</p>
          {kurse === null && <p className="fk-fuss">Termine werden geladen …</p>}
          {kurse?.length === 0 && (
            <p className="fk-fuss">Für diese Gruppe ist gerade alles voll —
              sprecht uns am Stand an, wir finden was.</p>
          )}
          {(kurse || []).slice(0, 8).map((k) => (
            <button key={k.termin_id} className="fk-pt-option" disabled={busy}
                    onClick={() => buchen(k.termin_id)}>
              <b>{k.wochentag} {k.start.slice(8, 10)}.{k.start.slice(5, 7)}.</b>{' '}
              {k.start.slice(11, 16)} Uhr · {k.kurs}
              <span className="fk-pt-frei">{k.frei} frei</span>
            </button>
          ))}
          <button className="fk-cta fk-cta-line" onClick={() => setSchritt(1)}>‹ Zurück</button>
        </div>
      )}
    </section>
  );
}


/* ------------------------------------------------------------------ CSS */

const CSS = `
.fk-root {
  min-height: 100vh; background: #0E0E10; color: #F5F5F4;
  font-family: Inter, system-ui, sans-serif; font-size: 16.5px; line-height: 1.55;
}
.fk-root * { box-sizing: border-box; }
.fk-root h1, .fk-root h2 {
  font-family: Anton, Inter, sans-serif; font-weight: 400;
  letter-spacing: .06em; text-transform: uppercase; margin: 0;
}
.fk-root a { color: inherit; }
.fk-kopf { padding: 34px 20px 6px; max-width: 560px; margin: 0 auto; }
.fk-pt-buchung { background: #17171B; border: 1px solid rgba(245,245,244,.14);
  border-radius: 12px; padding: 14px 16px; margin: 10px 0;
  display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.fk-pt-storno { background: none; border: 1px solid rgba(245,245,244,.25);
  color: #A8A8A8; border-radius: 9px; padding: 10px 12px; font: inherit;
  font-size: 13px; cursor: pointer; }
.fk-pt-ok { color: #3FBF6A; margin: 10px 0; }
.fk-pt-fehler { background: rgba(245,130,31,.08); border: 1px solid rgba(245,130,31,.4);
  border-radius: 12px; padding: 14px; margin: 10px 0; }
.fk-cta-gross { font-size: 18px; padding: 18px; }
.fk-pt-schritt { margin-top: 12px; }
.fk-pt-frage { font-weight: 700; margin: 12px 0 8px; }
.fk-pt-option { display: flex; align-items: center; gap: 10px; width: 100%;
  background: #17171B; border: 1px solid rgba(245,245,244,.18); color: inherit;
  border-radius: 12px; padding: 16px; margin: 8px 0; font: inherit;
  font-size: 16px; text-align: left; cursor: pointer; min-height: 56px; }
.fk-pt-option:active { border-color: #f5821f; }
.fk-pt-option:disabled { opacity: .5; }
.fk-pt-frei { margin-left: auto; color: #A8A8A8; font-size: 13px; white-space: nowrap; }
.fk-pt-input { width: 100%; background: #0E0E10; color: inherit;
  border: 1px solid rgba(245,245,244,.25); border-radius: 10px;
  padding: 14px; font: inherit; font-size: 16px; margin: 6px 0; }
.fk-logo { height: 44px; margin-bottom: 18px; }
.fk-eyebrow {
  color: #f5821f; font-size: 12.5px; letter-spacing: .2em;
  text-transform: uppercase; font-weight: 600; margin: 0 0 10px;
}
.fk-kopf h1 { font-size: clamp(34px, 9vw, 52px); line-height: 1.02; }
.fk-lead { opacity: .78; margin: 12px 0 0; max-width: 42ch; }
.fk-formular, .fk-karte { padding: 18px 20px 60px; max-width: 560px; margin: 0 auto; }
.fk-mitte { min-height: 70vh; display: flex; flex-direction: column; gap: 18px;
  align-items: center; justify-content: center; padding: 20px; text-align: center; }
.fk-lade { opacity: .7; }

.fk-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 420px) { .fk-grid { grid-template-columns: 1fr; } }
.fk-feld { display: flex; flex-direction: column; margin-bottom: 14px; }
.fk-feld span { font-size: 13px; opacity: .7; margin-bottom: 6px; }
.fk-feld input, .fk-feld select {
  font: inherit; color: inherit; background: #1A1A1D;
  border: 1px solid rgba(255,255,255,.14); border-radius: 12px;
  padding: 14px 14px; width: 100%; min-height: 50px;
}
.fk-feld input:focus, .fk-feld select:focus { outline: none; border-color: #f5821f; }

.fk-frage { margin: 10px 0 10px; font-weight: 600; }
.fk-chips { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
.fk-chip {
  font: inherit; color: inherit; cursor: pointer;
  background: #1A1A1D; border: 1px solid rgba(255,255,255,.16);
  border-radius: 999px; padding: 12px 18px; min-height: 48px;
}
.fk-chip-an { background: #f5821f; border-color: #f5821f; font-weight: 600; }
.fk-kinder { background: #151517; border: 1px solid rgba(255,255,255,.1);
  border-radius: 14px; padding: 14px 14px 2px; margin-bottom: 16px; }

.fk-haken { display: flex; gap: 12px; align-items: flex-start; margin: 0 0 14px; cursor: pointer; }
.fk-haken input { width: 22px; height: 22px; margin-top: 2px; accent-color: #f5821f; flex: none; }
.fk-haken span { font-size: 13.5px; opacity: .85; }

.fk-haftung {
  background: #151517; border: 1px solid rgba(255,255,255,.14);
  border-radius: 16px; padding: 18px 18px 16px; margin: 4px 0 18px;
}
.fk-haftung[data-fehler="true"] { border-color: #f5821f; }
.fk-haftung h2 {
  font-family: Anton, Inter, sans-serif; font-weight: 400; font-size: 16px;
  letter-spacing: .04em; text-transform: uppercase; color: #f5821f; margin: 0 0 10px;
}
.fk-haftung-text { font-size: 13.5px; line-height: 1.6; opacity: .82; margin: 0 0 16px; }
.fk-haken-haftung { margin: 0; padding-top: 14px; border-top: 1px solid rgba(255,255,255,.1); }
.fk-hp { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }

.fk-fehler { color: #FF8A80; border-left: 3px solid #f5821f; padding-left: 12px; }
.fk-cta {
  display: block; width: 100%; text-align: center; text-decoration: none;
  font: inherit; font-weight: 700; font-size: 17px; color: #fff; cursor: pointer;
  background: #f5821f; border: none; border-radius: 999px;
  padding: 17px 24px; margin-top: 10px;
}
.fk-cta:disabled { opacity: .55; }
.fk-cta-line { background: transparent; border: 1px solid rgba(255,255,255,.25);
  font-weight: 500; font-size: 15px; }
.fk-fuss { text-align: center; font-size: 13px; opacity: .55; margin-top: 12px; }

.fk-dankekarte {
  background: #151517; border: 1px solid rgba(255,255,255,.12);
  border-radius: 22px; padding: 30px 22px 26px; text-align: center;
  margin-bottom: 28px;
}
.fk-logo-danke { height: 34px; margin: 0 auto 16px; }
.fk-hallo { font-size: clamp(30px, 8vw, 44px); margin-top: 6px; }
.fk-danketext { opacity: .78; margin: 10px auto 0; max-width: 36ch; font-size: 15px; }
.fk-los { display: flex; flex-direction: column; align-items: center;
  background: #f5821f; border-radius: 18px; padding: 18px; margin: 22px 0 20px; }
.fk-los-label { font-size: 13px; letter-spacing: .14em; text-transform: uppercase; opacity: .85; }
.fk-los-nr { font-family: Anton, sans-serif; font-size: clamp(56px, 16vw, 84px); line-height: 1.05; }
.fk-qr { display: flex; justify-content: center; }
.fk-qr img { background: #fff; padding: 12px; border-radius: 16px; }
/* Bestaetigung nach dem Scan am Eingang: der Gast sieht selbst, dass es
   geklappt hat, und fragt nicht am Stand nach. */
.fk-eingecheckt { margin: 14px auto 0; width: fit-content; font-weight: 700;
  font-size: 14.5px; color: #4ADE80; background: rgba(74,222,128,.12);
  border-radius: 999px; padding: 8px 16px; }
.fk-hinweis { text-align: center; opacity: .75; font-size: 14.5px; max-width: 38ch; margin: 14px auto 0; }

/* Founding Small Group: eine Zahl traegt den Abschnitt, der Rest bleibt
   ruhig. Der Kasten hebt sich nur durch den Akzentrand ab. */
.fk-founding { border: 1px solid rgba(245,130,31,.45); background: rgba(245,130,31,.07);
  border-radius: 16px; padding: 18px 18px 20px; margin-bottom: 26px; }
.fk-founding h2 { font-size: 19px; margin: 0 0 8px; }
.fk-founding-text { font-size: 14.5px; opacity: .85; margin: 0 0 14px; line-height: 1.5; }
.fk-founding-zaehler { font-size: 15px; margin: 0 0 14px; }
.fk-founding-zaehler b { font-family: Anton, sans-serif; font-weight: 400;
  font-size: 40px; line-height: 1; color: #f5821f; vertical-align: -4px;
  margin: 0 4px; }
.fk-founding-ok { color: #4ADE80; font-weight: 600; font-size: 15px; margin: 0; }
.fk-founding-fuss { font-size: 12.5px; opacity: .55; margin: 12px 0 0; }

.fk-fortschritt h2, .fk-pt h2 { font-size: 19px; margin-bottom: 6px; }
.fk-zaehler { color: #4ADE80; font-weight: 600; margin: 0 0 12px; }
.fk-kacheln { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 26px; }
@media (max-width: 380px) { .fk-kacheln { grid-template-columns: 1fr; } }
.fk-kachel {
  display: flex; align-items: center; gap: 10px; font-size: 14.5px;
  background: #1A1A1D; border: 1px solid rgba(255,255,255,.12);
  border-radius: 12px; padding: 13px 14px; min-height: 52px;
}
.fk-kachel-an { background: rgba(74,222,128,.12); border-color: #4ADE80; }
.fk-kachel-haken { width: 22px; height: 22px; flex: none; border-radius: 50%;
  border: 1.5px solid rgba(255,255,255,.3); display: inline-flex;
  align-items: center; justify-content: center; font-size: 13px; }
.fk-kachel-an .fk-kachel-haken { background: #4ADE80; border-color: #4ADE80; color: #0E0E10; }
.fk-pt { background: #151517; border: 1px solid rgba(255,255,255,.1);
  border-radius: 14px; padding: 16px; margin-bottom: 22px; }
.fk-pt p { margin: 4px 0 0; opacity: .9; }
`;
